import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Menu from '../../components/menu/menu';
import Content from '../../components/content/content';
import DashboardCard from '../../components/components/dashboard_card/DashboardCard';
import ActivityItem from '../../components/components/activity_item/ActivityItem';
import './Dashboard.css';

import { ReactComponent as Ticket } from '../../components/icons/ticket.svg';
import { ReactComponent as PersonalCard } from '../../components/icons/personalcard.svg';
import { ReactComponent as UserEdit } from '../../components/icons/user-edit.svg';
import { ReactComponent as Danger } from '../../components/icons/danger.svg';

import ApiService from '../../components/api_services/apiService';
import {
  setAddEventMode,
  setEditedEvent,
  openEditModal,
  clearPendingEditReview,
  setPendingEditReview,
  setModalViewActualTab,
} from '../../store/eventsSlice';

function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    pendingEvents: 4,
    newOrganizers: 1,
    pendingDataChanges: 4,
    newReports: 0,
  });

  const [activityData, setActivityData] = useState([
    { id: 1, type: 'event', dateTime: '12.08.2025 10:30', title: 'Festiwal 1, Warszawa' },
    { id: 2, type: 'event', dateTime: '11.08.2025 12:22', title: 'Festiwal Kolor\u00f3w, Warszawa' },
    { id: 3, type: 'event', dateTime: '11.08.2025 12:00', title: 'Pokazy sztuk mistrz\u00f3w, Warszawa' },
    { id: 4, type: 'event', dateTime: '10.08.2025 09:10', title: 'Festiwal 1' },
    { id: 5, type: 'organizer', dateTime: '12.08.2025 12:45', title: 'Dom kultury Praga P\u00f3\u0142noc' },
    { id: 6, type: 'dataChange', dateTime: '12.08.2025 12:45', title: 'Dom kultury Praga P\u00f3\u0142noc' },
    { id: 7, type: 'dataChange', dateTime: '12.08.2025 12:45', title: 'Dom kultury Praga P\u00f3\u0142noc' },
    { id: 8, type: 'dataChange', dateTime: '12.08.2025 12:45', title: 'Dom kultury Praga P\u00f3\u0142noc' },
    { id: 9, type: 'dataChange', dateTime: '12.08.2025 12:45', title: 'Dom kultury Praga P\u00f3\u0142noc' },
  ]);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const normalizeListFromResponse = (response) => {
    const payload = response?.data ?? response;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.content)) return payload.content;
    return [];
  };

  const getTotalCountFromResponse = (response, fallbackLength) => {
    const payload = response?.data ?? response;
    const candidates = [
      payload?.totalCount,
      payload?.totalElements,
      payload?.count,
      payload?.allSize,
      response?.totalCount,
      response?.totalElements,
      response?.count,
      response?.allSize,
    ];

    const resolved = candidates.find((value) => Number.isFinite(Number(value)));
    return resolved !== undefined ? Number(resolved) : fallbackLength;
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [eventsResponse, organizersResponse, pendingEditsResponse, reportsResponse] = await Promise.all([
        ApiService.getEvents2(),
        ApiService.getOnlyNotApprovedOrganisers(),
        ApiService.getEvents(null, null, 1, null, 'PENDING_EDITS', 10),
        ApiService.getUserReports(),
      ]);

      const events = normalizeListFromResponse(eventsResponse);
      const organizers = normalizeListFromResponse(organizersResponse);
      const pendingEdits = normalizeListFromResponse(pendingEditsResponse);
      const reports = normalizeListFromResponse(reportsResponse);

      const eventCount = getTotalCountFromResponse(eventsResponse, events.length);
      const organizersCount = getTotalCountFromResponse(organizersResponse, organizers.length);
      const pendingEditsCount = getTotalCountFromResponse(pendingEditsResponse, pendingEdits.length);
      const reportsCount = getTotalCountFromResponse(reportsResponse, reports.length);

      const eventActivities = events.map((event, index) => ({
        id: `event-${event.id || index}`,
        eventId: event.id,
        type: 'event',
        dateTime: formatDateTime(event.startDate || event.added_on_utc),
        title: `${event.name}${event.city ? `, ${event.city}` : ''}`,
      }));

      const organizerActivities = organizers.map((organizer, index) => {
        const organizerId = organizer?.id ?? organizer?.organiserProfileId ?? organizer?.organizerProfileId ?? index;
        const organizerName = organizer?.companyName || organizer?.name || organizer?.fullName || `Organizator #${organizerId}`;
        const rawDate =
          organizer?.createdAt ||
          organizer?.addedOnUtc ||
          organizer?.added_on_utc ||
          organizer?.registrationDate ||
          organizer?.created_on_utc;

        return {
          id: `org-${organizerId}`,
          organizerId,
          type: 'organizer',
          dateTime: rawDate ? formatDateTime(rawDate) : '',
          title: organizerName,
        };
      });

      const reportActivities = reports.map((report, index) => {
        const reportId = report?.reportId ?? index;
        return {
          id: `rep-${reportId}`,
          reportId,
          type: 'report',
          dateTime: '',
          title: report?.reason || `Zgloszenie #${reportId}`,
        };
      });

      const dataChangeActivities = pendingEdits.map((item, index) => {
        const eventId = item?.id ?? index;
        const eventName = item?.name || `Wydarzenie #${eventId ?? '-'}`;
        const requestedAt = item?.pendingEditRequestedAt ?? item?.pendingRequestedAt;

        return {
          id: `dc-${eventId}`,
          eventId,
          type: 'dataChange',
          dateTime: formatDateTime(requestedAt),
          title: eventName,
        };
      });

      setActivityData([
        ...eventActivities,
        ...organizerActivities,
        ...dataChangeActivities,
        ...reportActivities,
      ]);

      setDashboardData((prev) => ({
        ...prev,
        pendingEvents: eventCount,
        newOrganizers: organizersCount,
        pendingDataChanges: pendingEditsCount,
        newReports: reportsCount,
      }));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShowAll = (type) => {
    switch (type) {
      case 'events':
        navigate('/events');
        break;
      case 'organizers':
        navigate('/organizers');
        break;
      case 'dataChanges':
        navigate('/events', { state: { filterQuery: 'PENDING_EDITS' } });
        break;
      case 'reports':
        navigate('/reports');
        break;
      default:
        break;
    }
  };

  const parsePendingPayload = (rawPayload) => {
    if (rawPayload && typeof rawPayload === 'object') {
      return rawPayload;
    }

    try {
      const parsed = JSON.parse(rawPayload || '{}');
      if (typeof parsed === 'string') {
        return JSON.parse(parsed || '{}');
      }
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  };

  const handleActivityClick = async (item) => {
    switch (item.type) {
      case 'event': {
        const eventId = Number(item?.eventId ?? String(item?.id || '').replace('event-', ''));
        if (Number.isFinite(eventId)) {
          dispatch(setAddEventMode(false));
          dispatch(clearPendingEditReview());
          dispatch(setModalViewActualTab('Dane podstawowe'));
          dispatch(setEditedEvent(eventId));
          dispatch(openEditModal());
        }
        navigate('/events');
        break;
      }
      case 'organizer':
        navigate('/organizers');
        break;
      case 'dataChange':
        if (Number.isFinite(Number(item?.eventId))) {
          const eventId = Number(item.eventId);
          let pendingItem = null;

          try {
            const pendingEdits = await ApiService.getPendingEventEdits();
            const list = Array.isArray(pendingEdits) ? pendingEdits : [];
            pendingItem = list.find((entry) => String(entry?.eventId) === String(eventId)) || null;
          } catch (error) {
            console.error('Error fetching pending edit details:', error);
          }

          dispatch(setAddEventMode(false));
          dispatch(setModalViewActualTab('Dane podstawowe'));
          dispatch(setEditedEvent(eventId));
          dispatch(clearPendingEditReview());
          dispatch(setPendingEditReview({
            eventId,
            pendingId: pendingItem?.id ?? pendingItem?.eventId ?? eventId,
            payload: parsePendingPayload(pendingItem?.pendingChange?.payloadJson),
            requestedAt: pendingItem?.pendingChange?.requestedAt ?? null,
          }));
          dispatch(openEditModal());
        }
        navigate('/events', { state: { filterQuery: 'PENDING_EDITS' } });
        break;
      case 'report':
        navigate('/reports');
        break;
      default:
        break;
    }
  };

  const groupedActivities = {
    events: activityData.filter((a) => a.type === 'event'),
    organizers: activityData.filter((a) => a.type === 'organizer'),
    dataChanges: activityData.filter((a) => a.type === 'dataChange'),
    reports: activityData.filter((a) => a.type === 'report'),
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <Menu />
        <Content>
          <div className="dashboard-loading">Ladowanie...</div>
        </Content>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <Menu />
      <Content>
        <div className="dashboard-container">
          <div className="dashboard-cards-row">
            <DashboardCard
              title="wydarzenia"
              count={dashboardData.pendingEvents}
              description="do zaakceptowania"
              backgroundColor="#EE8F3B"
              icon={Ticket}
              onShowAll={() => handleShowAll('events')}
            />
            <DashboardCard
              title="organizatorzy"
              count={dashboardData.newOrganizers}
              description="nowy"
              backgroundColor="#7D959F"
              icon={PersonalCard}
              onShowAll={() => handleShowAll('organizers')}
            />
            <DashboardCard
              title="Zmiana danych"
              count={dashboardData.pendingDataChanges}
              description="do zaakceptowania"
              backgroundColor="#201E25"
              icon={UserEdit}
              onShowAll={() => handleShowAll('dataChanges')}
            />
            <DashboardCard
              title="zgloszenia"
              count={dashboardData.newReports}
              description="nowy"
              backgroundColor="#DD2626"
              icon={Danger}
              onShowAll={() => handleShowAll('reports')}
            />
          </div>

          <div className="dashboard-activity-section">
            <div className="dashboard-activity-column">
              <div className="dashboard-activity-header">
                <div className="dashboard-activity-indicator" style={{ backgroundColor: '#EE8F3B' }} />
                <span>Wydarzenia do akceptacji</span>
              </div>
              <div className="dashboard-activity-list">
                {groupedActivities.events.map((item) => (
                  <ActivityItem
                    key={item.id}
                    type={item.type}
                    dateTime={item.dateTime}
                    title={item.title}
                    onClick={() => handleActivityClick(item)}
                  />
                ))}
              </div>
            </div>

            <div className="dashboard-activity-column">
              <div className="dashboard-activity-header">
                <div className="dashboard-activity-indicator" style={{ backgroundColor: '#7D959F' }} />
                <span>Nowi organizatorzy</span>
              </div>
              <div className="dashboard-activity-list">
                {groupedActivities.organizers.map((item) => (
                  <ActivityItem
                    key={item.id}
                    type={item.type}
                    dateTime={item.dateTime}
                    title={item.title}
                    onClick={() => handleActivityClick(item)}
                  />
                ))}
              </div>
            </div>

            <div className="dashboard-activity-column">
              <div className="dashboard-activity-header">
                <div className="dashboard-activity-indicator" style={{ backgroundColor: '#201E25' }} />
                <span>Zmiany danych do akceptacji</span>
              </div>
              <div className="dashboard-activity-list">
                {groupedActivities.dataChanges.map((item) => (
                  <ActivityItem
                    key={item.id}
                    type={item.type}
                    dateTime={item.dateTime}
                    title={item.title}
                    onClick={() => handleActivityClick(item)}
                  />
                ))}
              </div>
            </div>

            <div className="dashboard-activity-column">
              <div className="dashboard-activity-header">
                <div className="dashboard-activity-indicator" style={{ backgroundColor: '#DD2626' }} />
                <span>Nowe zgloszenia</span>
              </div>
              <div className="dashboard-activity-list">
                {groupedActivities.reports.map((item) => (
                  <ActivityItem
                    key={item.id}
                    type={item.type}
                    dateTime={item.dateTime}
                    title={item.title}
                    onClick={() => handleActivityClick(item)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </Content>
    </div>
  );
}

export default Dashboard;
